import React, { useState, useEffect } from 'react'
import { Type, Clock, Volume2, AlertCircle, Play, Pause } from 'lucide-react'
import { tts_duration_seconds, extractTTSVariables, validateTTSText, replaceTTSVariables } from '../utils/ttsUtils'

/**
 * TTS Element Properties Component
 * Handles the properties panel for TTS (Text-to-Speech) elements
 */
const TTSElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage
}) => {
  const [previewPlaying, setPreviewPlaying] = useState(false)
  const [validationResult, setValidationResult] = useState({ isValid: true, issues: [] })

  if (element.type !== 'tts') {
    return null
  }

  const text = element.text || ''
  const voice = element.voice || 'default'
  const wpm = element.wpm || 160
  const volume = element.settings?.volume || 1
  const fadeIn = element.settings?.fadeIn || 0
  const fadeOut = element.settings?.fadeOut || 0
  const pan = element.settings?.pan || 0

  // Available voice options
  const voiceOptions = [
    { value: 'default', label: 'Default Voice' },
    { value: 'male', label: 'Male Voice' },
    { value: 'female', label: 'Female Voice' },
    { value: 'child', label: 'Child Voice' }
  ]

  // Validate text and calculate duration
  useEffect(() => {
    const result = validateTTSText(text)
    setValidationResult(result)
    
    // Update duration when text or WPM changes
    if (result.isValid) {
      const newDuration = tts_duration_seconds(text, wpm)
      if (element.time?.duration !== newDuration) {
        updateElementProperty(selectedElement, 'time', {
          ...element.time,
          duration: newDuration
        })
      }
    }
  }, [text, wpm, selectedElement, updateElementProperty, element.time])

  const duration = tts_duration_seconds(text, wpm)
  const variables = extractTTSVariables(text)

  const handleTextChange = (newText) => {
    updateElementProperty(selectedElement, 'text', newText)
    
    // Update name to reflect new text
    const newName = `TTS: ${newText.substring(0, 30)}${newText.length > 30 ? '...' : ''}`
    updateElementProperty(selectedElement, 'name', newName)
  }

  const handleVoiceChange = (newVoice) => {
    updateElementProperty(selectedElement, 'voice', newVoice)
  }

  const handleWpmChange = (newWpm) => {
    updateElementProperty(selectedElement, 'wpm', newWpm)
  }

  const handleVolumeChange = (newVolume) => {
    updateElementProperty(selectedElement, 'settings', {
      ...element.settings,
      volume: newVolume
    })
  }

  const handleFadeInChange = (newFadeIn) => {
    updateElementProperty(selectedElement, 'settings', {
      ...element.settings,
      fadeIn: newFadeIn
    })
  }

  const handleFadeOutChange = (newFadeOut) => {
    updateElementProperty(selectedElement, 'settings', {
      ...element.settings,
      fadeOut: newFadeOut
    })
  }

  const handlePanChange = (newPan) => {
    updateElementProperty(selectedElement, 'settings', {
      ...element.settings,
      pan: newPan
    })
  }

  const handlePreviewTTS = () => {
    // This would integrate with a TTS preview service
    console.log('🗣️ Preview TTS:', { text, voice, wpm })
    setPreviewPlaying(!previewPlaying)
    
    // Simulate preview duration
    if (!previewPlaying) {
      setTimeout(() => {
        setPreviewPlaying(false)
      }, Math.min(duration * 1000, 10000)) // Max 10 seconds preview
    }
  }

  return (
    <div className="space-y-4">
      {/* TTS Content Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
          <Type className="w-4 h-4 mr-2" />
          TTS Content
        </h3>
        
        <div className="space-y-3">
          {/* Text Content */}
          <div className="space-y-2">
            <label className="text-sm text-gray-600">Text</label>
            <textarea
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder="Enter text to convert to speech..."
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              maxLength={5000}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{text.length}/5000 characters</span>
              <span className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                ~{duration.toFixed(1)}s
              </span>
            </div>
          </div>

          {/* Validation Messages */}
          {!validationResult.isValid && (
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <div className="flex items-center space-x-1 text-red-800 text-xs">
                <AlertCircle className="w-3 h-3" />
                <span className="font-medium">Issues:</span>
              </div>
              <ul className="mt-1 text-xs text-red-700 list-disc list-inside">
                {validationResult.issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Variables */}
          {variables.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-2">
              <div className="text-xs text-blue-800 font-medium mb-1">Variables:</div>
              <div className="flex flex-wrap gap-1">
                {variables.map((variable, index) => (
                  <span
                    key={index}
                    className="inline-block bg-blue-100 text-blue-800 text-xs px-1 py-0.5 rounded"
                  >
                    {variable}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Preview Button */}
          <button
            onClick={handlePreviewTTS}
            disabled={!validationResult.isValid || !text.trim()}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {previewPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            <span>{previewPlaying ? 'Stop Preview' : 'Preview TTS'}</span>
          </button>
        </div>
      </div>

      {/* Voice Settings Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Voice Settings</h3>
        
        <div className="space-y-3">
          {/* Voice Selection */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Voice</label>
            <select
              value={voice}
              onChange={(e) => handleVoiceChange(e.target.value)}
              className="flex-1 max-w-[120px] px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {voiceOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Speaking Speed */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Speed</label>
            <div className="flex items-center space-x-2 flex-1 max-w-[120px]">
              <input
                type="range"
                min="80"
                max="300"
                step="10"
                value={wpm}
                onChange={(e) => handleWpmChange(parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{wpm}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Audio Settings Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
          <Volume2 className="w-4 h-4 mr-2" />
          Audio Settings
        </h3>
        
        <div className="space-y-3">
          {/* Volume */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Volume</label>
            <div className="flex items-center space-x-2 flex-1 max-w-[120px]">
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{Math.round(volume * 100)}%</span>
            </div>
          </div>

          {/* Fade In */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Fade In</label>
            <div className="flex items-center space-x-2 flex-1 max-w-[120px]">
              <input
                type="range"
                min="0"
                max="5"
                step="0.1"
                value={fadeIn}
                onChange={(e) => handleFadeInChange(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{fadeIn.toFixed(1)}s</span>
            </div>
          </div>

          {/* Fade Out */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Fade Out</label>
            <div className="flex items-center space-x-2 flex-1 max-w-[120px]">
              <input
                type="range"
                min="0"
                max="5"
                step="0.1"
                value={fadeOut}
                onChange={(e) => handleFadeOutChange(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{fadeOut.toFixed(1)}s</span>
            </div>
          </div>

          {/* Pan */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[60px]">Pan</label>
            <div className="flex items-center space-x-2 flex-1 max-w-[120px]">
              <input
                type="range"
                min="-1"
                max="1"
                step="0.1"
                value={pan}
                onChange={(e) => handlePanChange(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">
                {pan === 0 ? 'C' : pan < 0 ? `L${Math.abs(pan * 100).toFixed(0)}` : `R${(pan * 100).toFixed(0)}`}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TTSElementProperties
